import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import RemediateModal from './RemediateModal';
import './CcmPanel.css';

const SEVERITY_COLOR = {
  CRITICAL: '#dc2626',
  HIGH:     '#ea580c',
  MEDIUM:   '#d97706',
};

const SEVERITY_BG = {
  CRITICAL: '#fee2e2',
  HIGH:     '#ffedd5',
  MEDIUM:   '#fef3c7',
};

const POLL_NORMAL   = 30000;
const POLL_ACTIVE   = 10000;
const RESOLVE_LINGER = 3500; // ms to show green state before removing

function CcmPanel() {
  const { t } = useTranslation();

  const timeAgo = (ts) => {
    const secs = Math.round((Date.now() - ts) / 1000);
    if (secs < 10) return t('CcmPanel.justNow');
    if (secs < 60) return t('CcmPanel.secondsAgo', { secs });
    return t('CcmPanel.minutesAgo', { mins: Math.round(secs / 60) });
  };

  const [alerts,          setAlerts]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [remediatingAlert, setRemediatingAlert] = useState(null);
  const [resolvedMap,     setResolvedMap]     = useState({}); // id → resolvedAt timestamp
  const [lastChecked,     setLastChecked]     = useState(null);
  const [showAll,         setShowAll]         = useState(false);
  const [, setTick] = useState(0); // drives "X ago" re-renders

  const hasActive = remediatingAlert !== null || Object.keys(resolvedMap).length > 0;
  const pollMs    = hasActive ? POLL_ACTIVE : POLL_NORMAL;

  // Re-render every 5s so "time ago" labels stay fresh
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/audit/ccm-alerts');
      // Don't overwrite alerts that are in "resolved" (green) linger state
      setAlerts(prev => {
        const incoming = res.data || [];
        const lingeringIds = new Set(
          Object.keys(resolvedMap).map(Number)
        );
        // Keep lingering resolved alerts, replace the rest
        const lingering = prev.filter(a => lingeringIds.has(a.auditProgressId));
        const fresh     = incoming.filter(a => !lingeringIds.has(a.auditProgressId));
        return [...fresh, ...lingering];
      });
      setLastChecked(Date.now());
    } catch {
      // keep previous state on network error
    } finally {
      setLoading(false);
    }
  }, [resolvedMap]);

  const pollRef = useRef(null);
  useEffect(() => {
    fetchAlerts();
    pollRef.current = setInterval(fetchAlerts, pollMs);
    return () => clearInterval(pollRef.current);
  }, [fetchAlerts, pollMs]);

  const handleRemediationDone = useCallback((alertId) => {
    setRemediatingAlert(null);
    // Animate to green
    setResolvedMap(prev => ({ ...prev, [alertId]: Date.now() }));
    // After linger, remove from both lists
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.auditProgressId !== alertId));
      setResolvedMap(prev => {
        const next = { ...prev };
        delete next[alertId];
        return next;
      });
    }, RESOLVE_LINGER);
  }, []);

  if (loading) {
    return (
      <div className="cc-panel">
        <div className="cc-header">
          <span className="cc-title">{t('CcmPanel.title')}</span>
          <span className="cc-live-dot" />
        </div>
        <div className="cc-empty">{t('CcmPanel.scanningControls')}</div>
      </div>
    );
  }

  const CCM_CAP = 5;
  const visibleAlerts = showAll ? alerts : alerts.slice(0, CCM_CAP);

  return (
    <div className="cc-panel">
      <div className="cc-header">
        <span className="cc-title">Continuous Control Monitoring</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {lastChecked && (
            <span className="cc-checked-at">
              {t('CcmPanel.checked', { time: timeAgo(lastChecked) })}
            </span>
          )}
          <span className="cc-live-badge">
            <span className={`cc-live-dot ${hasActive ? 'cc-live-fast' : ''}`} />
            {hasActive ? t('CcmPanel.tenSeconds') : t('CcmPanel.live')}
          </span>
        </div>
      </div>

      {visibleAlerts.length === 0 ? (
        <div className="cc-all-clear">
          <i className="fas fa-shield-alt cc-shield-icon" />
          <span>{t('CcmPanel.allClear')}</span>
        </div>
      ) : (
        <ul className="cc-alert-list">
          {visibleAlerts.map((alert) => {
            const isResolved = Boolean(resolvedMap[alert.auditProgressId]);
            const resolvedAt  = resolvedMap[alert.auditProgressId];
            const color = isResolved ? '#16a34a' : (SEVERITY_COLOR[alert.severity] || '#6b7280');
            const bg    = isResolved ? '#f0fdf4'  : (SEVERITY_BG[alert.severity]    || '#f9fafb');
            const border = isResolved ? '#bbf7d0' : '#f3f4f6';

            return (
              <li key={alert.auditProgressId}
                  className={`cc-alert-item ${isResolved ? 'cc-resolved' : ''}`}
                  style={{ background: bg, borderColor: border }}>
                <div className="cc-alert-left">
                  <span className="cc-sev-dot" style={{ background: color }} />
                  <div className="cc-alert-body">
                    {isResolved ? (
                      <>
                        <div className="cc-alert-msg cc-resolved-msg">
                          <i className="fas fa-check-circle" style={{ color: '#16a34a', marginRight: 6 }} />
                          {t('CcmPanel.controlResolved')}
                        </div>
                        <div className="cc-alert-meta">
                          <span className="cc-resolved-label">{alert.message}</span>
                          <span className="cc-verified-tag">
                            {t('CcmPanel.verified', { time: timeAgo(resolvedAt) })}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="cc-alert-msg">{alert.message}</div>
                        {alert.tscCode && (
                          <div className="cc-alert-meta">
                            <span className="cc-tsc-tag">{alert.tscCode}</span>
                            {alert.affectedResources?.length > 0 && (
                              <span className="cc-resources">
                                {alert.affectedResources.join(' · ')}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="cc-alert-right">
                  {isResolved ? (
                    <span className="cc-resolved-badge">{t('CcmPanel.resolvedBadge')}</span>
                  ) : (
                    <>
                      <span className="cc-sev-badge" style={{ color, background: bg }}>
                        {alert.severity}
                      </span>
                      <button
                        className="cc-remediate-btn"
                        onClick={() => setRemediatingAlert(alert)}
                      >
                        {t('CcmPanel.remediate')}
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {alerts.length > CCM_CAP && (
        <div
          className="cc-showmore"
          role="button"
          tabIndex={0}
          onClick={() => setShowAll(v => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowAll(v => !v); }}
          style={{ cursor: 'pointer', padding: '0.5rem 0', fontSize: '0.8rem', fontWeight: 600, color: '#4f46e5' }}
        >
          {showAll
            ? t('CcmPanel.showLess', { defaultValue: 'Show less' })
            : t('CcmPanel.showMore', { count: alerts.length - CCM_CAP, defaultValue: `Show ${alerts.length - CCM_CAP} more` })}
        </div>
      )}

      {remediatingAlert && (
        <RemediateModal
          alert={remediatingAlert}
          onClose={() => setRemediatingAlert(null)}
          onDone={handleRemediationDone}
        />
      )}
    </div>
  );
}

export default CcmPanel;
